import { useState, useEffect, useMemo, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Shield, Lock, LockOpen, LogOut, ChevronDown, ChevronRight,
  Search, GripVertical, Star, RotateCcw, X,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCapabilities } from '@/hooks/useCapabilities'
import {
  DEFAULT_NAV_GROUPS, ITEM_BY_TO, GROUP_TITLE_BY_ID,
  useNavLayout, useCanSeeNavItem,
  type NavItem, type NavGroup,
} from '@/hooks/useNavGroups'
import { clsx } from 'clsx'
import HouseholdSwitcher from './HouseholdSwitcher'
import InstallAppButton from '@/components/ui/InstallAppButton'

// The nav model (groups, entries, question-tooltips, capability gating,
// saved order/favourites) lives in @/hooks/useNavGroups — the single source
// of truth shared with TopNav's mobile menu and the CommandPalette. This
// file is purely the desktop-sidebar presentation: collapse state,
// hover-expand, drag-and-drop editing, favourites UI.

// Re-export for any legacy importers of the nav model.
export { DEFAULT_NAV_GROUPS }
export type { NavItem, NavGroup }

// ── Collapse state ─────────────────────────────────────────────────────────
const COLLAPSED_KEY = 'av-nav-collapsed'
const DEFAULT_COLLAPSED = new Set(['records', 'admin'])

function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY)
      if (raw) return new Set(JSON.parse(raw))
    } catch { /* ignore */ }
    return new Set(DEFAULT_COLLAPSED)
  })
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed])) } catch { /* */ }
  }, [collapsed])
  const toggle = (id: string) =>
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  // Accordion behaviour: collapse every group except the supplied one.
  // Called from the route-change effect so navigating to a page in a
  // different group auto-rolls-up the previous section.
  const collapseAllExcept = useCallback((keepId: string | null) => {
    if (!keepId) return
    setCollapsed(() => new Set(
      DEFAULT_NAV_GROUPS.map(g => g.id).filter(id => id !== keepId),
    ))
  }, [])
  return { collapsed, toggle, collapseAllExcept }
}

// ── DnD payloads encoded through dataTransfer ──────────────────────────────
type DragPayload =
  | { kind: 'group'; groupId: string }
  | { kind: 'item'; itemTo: string; fromGroupId: string }

function setDragPayload(e: React.DragEvent, payload: DragPayload) {
  e.dataTransfer.setData('application/json', JSON.stringify(payload))
  e.dataTransfer.effectAllowed = 'move'
}

function readDragPayload(e: React.DragEvent): DragPayload | null {
  try {
    const raw = e.dataTransfer.getData('application/json')
    if (!raw) return null
    return JSON.parse(raw) as DragPayload
  } catch { return null }
}

export default function SideNav() {
  const { user, logout } = useAuth()
  const { collapsed, toggle, collapseAllExcept } = useCollapsedGroups()
  const { layout, reset, moveGroup, moveItem, toggleFavourite } = useNavLayout()
  const location = useLocation()

  // Phase B — capability-aware visibility (shared with TopNav + palette).
  const canSee = useCanSeeNavItem()

  const [editMode, setEditMode] = useState(false)
  const [search, setSearch] = useState('')
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null)
  // Hover-to-expand was removed: expanding a group inline pushes every
  // group below it down, so sweeping the cursor over the sidebar made the
  // whole menu pump open and shut ("the menus jump around badly"). Groups
  // now open on CLICK only, with an animated roll (.nav-roll).

  // ── Accordion auto-rollup ───────────────────────────────────────────────
  // Find which group contains the current active route. Longest-prefix
  // match wins so '/app' (Command Centre, end:true) doesn't accidentally
  // claim every '/app/*' route.
  const activeGroupId = useMemo<string | null>(() => {
    const pathname = location.pathname
    let best: { groupId: string; len: number } | null = null
    for (const group of DEFAULT_NAV_GROUPS) {
      for (const item of group.items) {
        const matches = item.end
          ? pathname === item.to
          : pathname === item.to || pathname.startsWith(item.to + '/')
        if (matches && (!best || item.to.length > best.len)) {
          best = { groupId: group.id, len: item.to.length }
        }
      }
    }
    return best?.groupId ?? null
  }, [location.pathname])

  // Whenever the active group changes, collapse every other group so only
  // the section relevant to the current page stays expanded. Manual toggles
  // remain sticky until the next navigation.
  useEffect(() => {
    collapseAllExcept(activeGroupId)
  }, [activeGroupId, collapseAllExcept])

  // Build the render model: list of {group, items[]} plus a leading Favourites
  const renderGroups = useMemo<NavGroup[]>(() => {
    const out: NavGroup[] = []
    if (layout.favourites.length > 0) {
      out.push({
        id: '__favourites',
        title: 'Favourites',
        items: layout.favourites
          .map(to => ITEM_BY_TO[to])
          .filter(Boolean) as NavItem[],
      })
    }
    for (const gid of layout.groupOrder) {
      const tos = layout.itemsByGroup[gid] || []
      const items = tos.map(to => ITEM_BY_TO[to]).filter(Boolean) as NavItem[]
      out.push({ id: gid, title: GROUP_TITLE_BY_ID[gid] || gid, items })
    }
    return out
  }, [layout])

  // Search filter — matches on item label, shows all items across any group.
  // Filtering collapses group structure into one flat "Results" list.
  const q = search.trim().toLowerCase()
  const searchActive = q.length > 0
  const searchResults = useMemo<NavItem[]>(() => {
    if (!searchActive) return []
    const seen = new Set<string>()
    const out: NavItem[] = []
    for (const g of DEFAULT_NAV_GROUPS) {
      for (const it of g.items) {
        if (seen.has(it.to)) continue
        if (!canSee(it)) continue
        if (it.label.toLowerCase().includes(q)) {
          out.push(it)
          seen.add(it.to)
        }
      }
    }
    return out
  }, [q, searchActive, canSee])

  // ── Drag handlers (native HTML5) ─────────────────────────────────────
  const onGroupDrop = (e: React.DragEvent, targetGroupId: string) => {
    e.preventDefault()
    setDragOverGroup(null)
    const payload = readDragPayload(e)
    if (!payload) return
    if (payload.kind === 'group') {
      const fromIdx = layout.groupOrder.indexOf(payload.groupId)
      const toIdx = layout.groupOrder.indexOf(targetGroupId)
      if (fromIdx >= 0 && toIdx >= 0) moveGroup(fromIdx, toIdx)
    } else if (payload.kind === 'item') {
      // Drop onto group header → append to that group
      moveItem(payload.itemTo, payload.fromGroupId, targetGroupId, null)
    }
  }

  const onItemDrop = (e: React.DragEvent, targetGroupId: string, targetIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverGroup(null)
    const payload = readDragPayload(e)
    if (!payload || payload.kind !== 'item') return
    // If dropping within the same group and the index is past the source, adjust
    let idx = targetIdx
    if (payload.fromGroupId === targetGroupId) {
      const sourceIdx = (layout.itemsByGroup[targetGroupId] || []).indexOf(payload.itemTo)
      if (sourceIdx >= 0 && sourceIdx < targetIdx) idx -= 1
    }
    moveItem(payload.itemTo, payload.fromGroupId, targetGroupId, idx)
  }

  return (
    <nav
      className="glass hidden md:flex flex-col w-56 min-h-screen border-0 border-r border-border shrink-0"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-grad-blue flex items-center justify-center shrink-0 shadow-lg shadow-blue/20">
            <Shield size={18} className="text-white" aria-hidden />
          </div>
          <div>
            <p className="text-sm font-display font-bold text-text-primary leading-none">AgentVault</p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-text-muted mt-1">Capital</p>
          </div>
        </div>
      </div>

      {/* Search + Lock */}
      <div className="px-3 py-2 border-b border-border space-y-2">
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search nav..."
              className="w-full pl-7 pr-6 py-1.5 text-xs rounded border border-border bg-surface-card text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-blue"
              aria-label="Search navigation"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                aria-label="Clear search"
                title="Clear"
              >
                <X size={11} aria-hidden />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditMode(v => !v)}
            title={editMode ? 'Lock sidebar' : 'Edit sidebar order + favourites'}
            aria-label={editMode ? 'Lock sidebar' : 'Edit sidebar'}
            className={clsx(
              'shrink-0 p-1.5 rounded border transition',
              editMode
                ? 'border-amber-500/50 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25'
                : 'border-border bg-surface-card text-text-muted hover:text-text-primary',
            )}
          >
            {editMode
              ? <LockOpen size={12} aria-hidden />
              : <Lock size={12} aria-hidden />
            }
          </button>
          {editMode && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Reset sidebar order + favourites to defaults?')) reset()
              }}
              title="Reset to defaults"
              aria-label="Reset sidebar"
              className="shrink-0 p-1.5 rounded border border-border bg-surface-card text-text-muted hover:text-rose-light transition"
            >
              <RotateCcw size={12} aria-hidden />
            </button>
          )}
        </div>
        {editMode && (
          <p className="text-[10px] text-amber-400 leading-tight">
            Drag groups &amp; items with <GripVertical size={9} className="inline -mt-0.5" aria-hidden />. Click <Star size={9} className="inline -mt-0.5" aria-hidden /> to favourite.
          </p>
        )}
      </div>

      {/* Grouped Links */}
      <div className="flex-1 px-2 py-2 overflow-y-auto">
        {searchActive ? (
          <div className="space-y-0.5">
            <div className="px-3 py-1 panel-title text-[10px]">
              {searchResults.length} result{searchResults.length === 1 ? '' : 's'}
            </div>
            {searchResults.map(({ to, label, icon: Icon, end, tooltip }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={tooltip ?? label}
                onClick={() => setSearch('')}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-lg overflow-visible text-sm transition-colors duration-150',
                    isActive
                      ? 'nav-active'
                      : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                  )
                }
              >
                <Icon size={15} aria-hidden className="shrink-0" />
                {label}
              </NavLink>
            ))}
            {searchResults.length === 0 && (
              <p className="px-3 py-2 text-xs text-text-muted">No matches</p>
            )}
          </div>
        ) : (
          renderGroups.map((group) => {
            const isFavGroup = group.id === '__favourites'
            const isCollapsed = !isFavGroup && collapsed.has(group.id)
            return (
              <div
                key={group.id}
                className={clsx(
                  'mb-2 rounded',
                  editMode && dragOverGroup === group.id && 'ring-1 ring-amber-500/50 bg-amber-500/5',
                )}
                onDragOver={(e) => {
                  if (!editMode) return
                  e.preventDefault()
                  setDragOverGroup(group.id)
                }}
                onDragLeave={() => setDragOverGroup(null)}
                onDrop={(e) => editMode && !isFavGroup ? onGroupDrop(e, group.id) : undefined}
              >
                <div
                  className={clsx(
                    'w-full flex items-center gap-1 px-2 py-1.5 panel-title text-[10px] rounded',
                    isFavGroup && 'text-amber-400',
                  )}
                  draggable={editMode && !isFavGroup}
                  onDragStart={(e) => {
                    if (!editMode || isFavGroup) return
                    setDragPayload(e, { kind: 'group', groupId: group.id })
                  }}
                >
                  {editMode && !isFavGroup && (
                    <GripVertical size={10} className="text-text-muted cursor-grab shrink-0" aria-hidden />
                  )}
                  {isFavGroup && (
                    <Star size={10} className="text-amber-400 shrink-0" aria-hidden />
                  )}
                  <button
                    type="button"
                    onClick={() => !isFavGroup && toggle(group.id)}
                    disabled={isFavGroup}
                    className={clsx(
                      'flex-1 flex items-center justify-between text-left',
                      !isFavGroup && 'hover:text-text-secondary cursor-pointer',
                    )}
                    aria-controls={`nav-group-${group.id}`}
                    aria-label={isCollapsed ? `Expand ${group.title}` : `Collapse ${group.title}`}
                  >
                    <span>{group.title}</span>
                    {!isFavGroup && (
                      isCollapsed
                        ? <ChevronRight size={12} aria-hidden />
                        : <ChevronDown size={12} aria-hidden />
                    )}
                  </button>
                </div>
                <div className={clsx('nav-roll', !isCollapsed && 'nav-roll-open')}>
                  <div id={`nav-group-${group.id}`} className="nav-roll-inner mt-0.5 space-y-0.5">
                    {group.items
                      .filter(canSee)
                      .map(({ to, label, icon: Icon, end }, itemIdx) => {
                        const isFav = layout.favourites.includes(to)
                        return (
                          <div key={to}>
                            {/* Drop target between items (only in edit mode, not in fav group) */}
                            {editMode && !isFavGroup && (
                              <div
                                className="h-0.5 -my-px"
                                onDragOver={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  e.currentTarget.classList.add('bg-blue-400', 'h-1')
                                }}
                                onDragLeave={(e) => {
                                  e.currentTarget.classList.remove('bg-blue-400', 'h-1')
                                }}
                                onDrop={(e) => {
                                  e.currentTarget.classList.remove('bg-blue-400', 'h-1')
                                  onItemDrop(e, group.id, itemIdx)
                                }}
                              />
                            )}
                            <div
                              draggable={editMode && !isFavGroup}
                              onDragStart={(e) => {
                                if (!editMode || isFavGroup) return
                                setDragPayload(e, { kind: 'item', itemTo: to, fromGroupId: group.id })
                              }}
                              className={clsx(
                                'flex items-center gap-1',
                                editMode && !isFavGroup && 'cursor-grab',
                              )}
                            >
                              {editMode && !isFavGroup && (
                                <GripVertical size={10} className="text-text-muted shrink-0 ml-1" aria-hidden />
                              )}
                              <NavLink
                                to={to}
                                end={end}
                                title={ITEM_BY_TO[to]?.tooltip ?? label}
                                className={({ isActive }) =>
                                  clsx(
                                    'flex-1 flex items-center gap-3 px-3 py-2 rounded-lg overflow-visible text-sm transition-colors duration-150',
                                    isActive
                                      ? 'nav-active'
                                      : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                                  )
                                }
                                aria-label={label}
                              >
                                <Icon size={15} aria-hidden className="shrink-0" />
                                {label}
                              </NavLink>
                              {editMode && !isFavGroup && (
                                <button
                                  type="button"
                                  onClick={() => toggleFavourite(to)}
                                  title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                                  aria-label={isFav ? `Remove ${label} from favourites` : `Add ${label} to favourites`}
                                  className={clsx(
                                    'shrink-0 p-1 rounded hover:bg-surface-card transition',
                                    isFav ? 'text-amber-400' : 'text-text-muted hover:text-amber-400',
                                  )}
                                >
                                  <Star
                                    size={11}
                                    className={isFav ? 'fill-amber-300' : ''}
                                    aria-hidden
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    {/* Bottom drop zone — append to group */}
                    {editMode && !isFavGroup && (
                      <div
                        className="h-1.5"
                        onDragOver={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          e.currentTarget.classList.add('bg-blue-400')
                        }}
                        onDragLeave={(e) => e.currentTarget.classList.remove('bg-blue-400')}
                        onDrop={(e) => {
                          e.currentTarget.classList.remove('bg-blue-400')
                          onItemDrop(e, group.id, group.items.length)
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Install prompt — appears on Chrome/Edge/Android when eligible */}
      <div className="px-3 pb-2">
        <InstallAppButton />
      </div>

      {/* Household switcher */}
      <HouseholdSwitcher />

      {/* User + Logout */}
      <div className="px-3 pb-4 border-t border-border pt-4 space-y-1">
        {user && (
          <div className="px-3 py-2 mb-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-text-primary truncate">{user.name}</p>
              <TierBadge />
            </div>
            <p className="text-xs text-text-muted truncate">{user.title}</p>
          </div>
        )}
        <button
          type="button"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-text-secondary
                     hover:bg-rose/10 hover:text-rose-light transition-colors duration-150
                     focus-visible:ring-2 focus-visible:ring-rose focus-visible:ring-offset-1 focus-visible:ring-offset-surface-DEFAULT"
          aria-label="Sign out"
        >
          <LogOut size={16} aria-hidden className="shrink-0" />
          Sign Out
        </button>
      </div>
      <div className="px-4 py-2 border-t border-border">
        <p className="text-[10px] text-text-muted font-mono text-center">v1.4.0</p>
      </div>
    </nav>
  )
}


// ── User tier badge ─────────────────────────────────────────────────────────
//
// Small pill in the user-info section showing the active commercial tier.
// Owner gets a purple pill ("OWNER"), Advised emerald, Planner blue,
// Free grey, Demo amber. The capability manifest is the source of truth so
// this stays in sync with what the rest of the UI shows / hides.
function TierBadge() {
  const capabilities = useCapabilities((s) => s.capabilities)
  if (!capabilities) return null
  const tier = (capabilities.tier || 'free').toLowerCase()
  const styles: Record<string, string> = {
    owner:   'bg-purple/10 text-purple-light border-purple/40',
    advised: 'bg-emerald/10 text-emerald-light border-emerald/40',
    planner: 'bg-blue/10 text-blue-light border-blue/40',
    free:    'bg-surface-card text-text-muted border-border',
    demo:    'bg-amber/10 text-amber border-amber/40',
  }
  const klass = styles[tier] ?? styles.free
  return (
    <span
      className={clsx(
        'text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border',
        klass,
      )}
      title={`${tier} tier`}
    >
      {tier}
    </span>
  )
}
